// app_client/src/store/useTransactionStore.js
import { create } from 'zustand';
import axios from 'axios';
import useAccountStore from './useAccountStore';
import useFinancialStore from './useFinancialStore';
import useInvoiceStore from './useInvoiceStore';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const normalizeSearchTerm = (value) => String(value || '').trim();
const serializeTransactionParams = (params = {}) => JSON.stringify({
  search: normalizeSearchTerm(params.search),
  page: params.page ?? 1,
  financialAccountId: params.financialAccountId ?? '',
  startDate: params.startDate ?? '',
  endDate: params.endDate ?? '',
  type: params.type ?? 'all',
});

const getTransactionAllocatedAmount = (transaction) => (
  Array.isArray(transaction?.allocations)
    ? transaction.allocations.reduce((sum, allocation) => (
        sum + Number(allocation?.sourceAmount ?? allocation?.amount ?? 0)
      ), 0)
    : 0
);

const getTransactionRemainingAmount = (transaction) => {
  const remaining = transaction?.remainingAmount ?? transaction?.amount ?? 0;
  const normalizedRemaining = Number(remaining);
  if (Number.isFinite(normalizedRemaining) && normalizedRemaining >= 0) {
    return normalizedRemaining;
  }

  const totalAmount = Number(transaction?.amount ?? 0);
  return Math.max(0, totalAmount - getTransactionAllocatedAmount(transaction));
};

const useTransactionStore = create((set, get) => ({
  transactions: [],
  totalPages: 1,
  currentPage: 1,
  loading: false,
  lastParams: {}, // Son kullanılan filtreleri saklayalım ki tazeleyebilelim
  lastParamsKey: '',

  fetchTransactions: async (params = {}) => {
    const nextParams = {
      search: normalizeSearchTerm(params.search),
      page: params.page ?? 1,
      financialAccountId: params.financialAccountId ?? '',
      startDate: params.startDate ?? '',
      endDate: params.endDate ?? '',
      type: params.type ?? 'all',
    };
    const nextParamsKey = serializeTransactionParams(nextParams);
    const { loading, lastParamsKey } = get();

    if (loading && lastParamsKey === nextParamsKey) {
      return;
    }

    set({ loading: true, lastParams: nextParams, lastParamsKey: nextParamsKey });
    try {
      const res = await axios.get('/api/transactions', { params: nextParams });
      set({ 
        transactions: res.data.data, 
        totalPages: res.data.totalPages,
        currentPage: res.data.currentPage,
        loading: false 
      });
    } catch {
      set({ loading: false });
    }
  },

  fetchAccountTransactions: async (accountId) => {
    set({ loading: true });
    try {
      const res = await axios.get(`/api/transactions/account/${accountId}`);

      //console.log('RES', res)
    
      set({ 
        transactions: res.data, // Doğrudan array olarak setliyoruz
        loading: false 
      });
    } catch (error) {
      set({ loading: false });
      console.error("Ekstre yükleme hatası:", error);
    }
  },

  fetchTransactionDependencies: async (transactionId) => {
    try {
      const res = await axios.get(`/api/transactions/${transactionId}/dependencies`);
      return {
        success: true,
        data: res.data.data
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "İşlem bağlantıları alınamadı."
      };
    }
  },

  getMatchingInvoices: async (transactionId) => {
    const res = await axios.get(`/api/transactions/${transactionId}/open-invoices`);
    return Array.isArray(res.data?.data) ? res.data.data : [];
  },

  getAvailableInvoices: async (accountId) => {
    if (!accountId) return [];

    const res = await axios.get('/api/invoices', {
      params: {
        accountId,
        page: 1,
        limit: 250
      }
    });

    const invoices = Array.isArray(res.data?.data) ? res.data.data : [];
    return invoices.filter((invoice) => (
      invoice.status !== 'Cancelled' &&
      ['Unpaid', 'PartiallyPaid'].includes(invoice.paymentStatus || 'Unpaid') &&
      Number(invoice.remainingAmount ?? invoice.grandTotal ?? 0) > 0
    )).map((invoice) => ({
      ...invoice,
      remainingAmount: Number(invoice.remainingAmount ?? invoice.grandTotal ?? 0)
    }));
  },

  getAvailablePayments: async (accountId) => {
    if (!accountId) return [];

    const res = await axios.get('/api/transactions', {
      params: {
        accountId,
        page: 1,
        limit: 250
      }
    });

    const payments = Array.isArray(res.data?.data) ? res.data.data : [];
    const normalizedPayments = payments.map((transaction) => {
      const normalizedAccount = Array.isArray(transaction?.account)
        ? transaction.account[0] || null
        : transaction?.account || null;
      const remainingAmount = Number(transaction?.remainingAmount ?? transaction?.amount ?? 0);
      const availableAmount = Number.isFinite(remainingAmount) && remainingAmount >= 0
        ? remainingAmount
        : getTransactionRemainingAmount(transaction);

      return {
        ...transaction,
        account: normalizedAccount,
        remainingAmount,
        availableAmount
      };
    });

    const rejectedReasons = normalizedPayments.reduce((acc, transaction) => {
      if (!transaction) {
        acc.empty = (acc.empty || 0) + 1;
        return acc;
      }
      if (transaction.isTransfer) acc.transfer = (acc.transfer || 0) + 1;
      if (transaction.category === 'Transfer') acc.transferCategory = (acc.transferCategory || 0) + 1;
      if (transaction.isCancelled === true) acc.cancelled = (acc.cancelled || 0) + 1;
      if (!['Inflow', 'Outflow'].includes(transaction.type)) acc.type = (acc.type || 0) + 1;
      if (!(Number(transaction.availableAmount ?? 0) > 0)) acc.noRemaining = (acc.noRemaining || 0) + 1;
      return acc;
    }, {});

    const visiblePayments = normalizedPayments.filter((transaction) => (
        transaction &&
        !transaction.isTransfer &&
        transaction.category !== 'Transfer' &&
        transaction.isCancelled !== true &&
        ['Inflow', 'Outflow'].includes(transaction.type) &&
        transaction.availableAmount > 0
      ));

    // Debug: clearing ekranında hangi kayıtların neden elendiğini görmek için.
    console.log('[useTransactionStore:getAvailablePayments]', {
      accountId,
      total: payments.length,
      visible: visiblePayments.length,
      rejectedReasons
    });

    return visiblePayments;
  },

  getRecentAllocations: async (accountId) => {
    if (!accountId) return [];

    const res = await axios.get('/api/transactions', {
      params: {
        accountId,
        page: 1,
        limit: 250
      }
    });

    const transactions = Array.isArray(res.data?.data) ? res.data.data : [];

    return transactions
      .flatMap((transaction) => (
        Array.isArray(transaction.allocations)
          ? transaction.allocations.map((allocation) => ({
              ...allocation,
              transactionId: transaction._id,
              transactionCategory: transaction.category || transaction.type || '',
              transactionDate: transaction.date,
              transactionNumber: transaction.transactionNumber || transaction._id,
              transactionDescription: transaction.description || '',
              invoiceNumber: allocation.invoiceId?.invoiceNumber || '',
              invoiceCurrency: allocation.invoiceCurrency || allocation.invoiceId?.currency || 'TRY',
              sourceCurrency: allocation.sourceCurrency || transaction.financialAccount?.currency || transaction.currency || 'TRY',
              sourceAmount: Number(allocation.sourceAmount ?? allocation.amount ?? 0),
              allocatedAmount: Number(allocation.allocatedAmount ?? 0),
              exchangeRate: Number(allocation.exchangeRate ?? 1),
              fxDifferenceAmount: Number(allocation.fxDifferenceAmount ?? 0),
              fxDifferenceType: allocation.fxDifferenceType || 'None',
              allocatedAt: allocation.allocatedAt || allocation.createdAt || transaction.date
            }))
          : []
      ))
      .filter((allocation) => allocation?.invoiceId)
      .sort((a, b) => new Date(b.allocatedAt || b.createdAt || b.date || 0) - new Date(a.allocatedAt || a.createdAt || a.date || 0))
      .slice(0, 25);
  },

  allocate: async (transactionId, payloadOrInvoiceId, amount = null) => {
    try {
      const payload = typeof payloadOrInvoiceId === 'object' && payloadOrInvoiceId !== null
        ? { ...payloadOrInvoiceId }
        : { invoiceId: payloadOrInvoiceId };

      if (amount !== null && amount !== undefined && amount !== '') {
        payload.amount = Number(amount);
      }

      const res = await axios.post(`/api/transactions/${transactionId}/allocate`, payload);
      await get().fetchTransactions(get().lastParams);
      await useInvoiceStore.getState().fetchInvoices(useInvoiceStore.getState().lastParams);
      return { success: true, data: res.data.data || res.data };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Fatura eşleştirilemedi.'
      };
    }
  },

  unlinkAllocation: async (transactionId, allocationOrInvoice) => {
    try {
      const payload = typeof allocationOrInvoice === 'object' && allocationOrInvoice !== null
        ? { ...allocationOrInvoice }
        : { invoiceId: allocationOrInvoice };

      const res = await axios.post(`/api/transactions/${transactionId}/unallocate`, payload);
      await get().fetchTransactions(get().lastParams);
      await useInvoiceStore.getState().fetchInvoices(useInvoiceStore.getState().lastParams);
      return { success: true, data: res.data.data || res.data };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Fatura ilişkisi kaldırılamadı.'
      };
    }
  },

  addTransaction: async (data) => {
    try {
      const res = await axios.post('/api/transactions', data);
      
      // 1. Cari (Account) Bakiyesini Yenile
      if (data.accountId) {
        useAccountStore.getState().fetchAccountById(data.accountId);
      }
      
      // 2. Kasa/Banka (FinancialAccount) Bakiyelerini Yenile
      useFinancialStore.getState().fetchAccounts();
      
      // 3. Mevcut İşlem Listesini Yenile (Tablo güncellensin)
      get().fetchTransactions(get().lastParams);

      if (Array.isArray(data.allocations) && data.allocations.length > 0) {
        useInvoiceStore.getState().fetchInvoices(useInvoiceStore.getState().lastParams);
      }
      
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.message };
    }
  },

  deleteTransaction: async (transactionId, reason = '') => {
    try {
      const tx = get().transactions.find(t => t._id === transactionId);
      const accId = tx?.account?._id || tx?.account || tx?.accountId?._id || tx?.accountId; // Silinen kayıttaki cari ID'yi al
      const allocationInvoiceIds = (tx?.allocations || [])
        .map(allocation => allocation?.invoiceId?._id || allocation?.invoiceId)
        .filter(Boolean);
      const { transactions, currentPage, lastParams } = get();
      const nextPage = transactions.length === 1 && currentPage > 1
        ? currentPage - 1
        : currentPage;

      const response = await axios.delete(`/api/transactions/${transactionId}`, { data: { reason } });
      if (response.data?.success === false) {
        return { success: false, message: response.data?.message || "İşlem silinemedi." };
      }

      await delay(300);
    
      await get().fetchTransactions({ ...lastParams, page: nextPage });
    
      await useFinancialStore.getState().fetchAccounts();
      if (accId) {
        await useAccountStore.getState().fetchAccountById(accId);
      }
      if (allocationInvoiceIds.length > 0) {
        await useInvoiceStore.getState().fetchInvoices(useInvoiceStore.getState().lastParams);
      }
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.response?.data?.error || "İşlem silinemedi."
      };
    }
  },

  addTransfer: async (data) => {
    // Virman işlemi aslında bir transaction'dır ama backend'e category: Transfer gider
    return get().addTransaction({ ...data, category: 'Transfer' });
  }
}));

export default useTransactionStore;
